import { ColumnType } from '@datachain/ui-sdk/components/dc-types';
import { immerable, produce } from 'immer';
import { Map, Set } from 'immutable';

import { EnumValues } from '../../../core/utils/type.utils';

/**
 * ARRAY => DepotSchemaEntity.buildArray()
 * ARRAY => DepotSchemaEntity.buildElement() + isList: true
 */

export const EMPTY_SCHEMA_HEADER_LABEL = 'EMPTY_LABEL';

export type SchemaSelectedHeader = { headerName: string; headerType: string };

export type DepotSchemaId = string | number;

export enum SchemaDataStructure {
	Root = 'ROOT',
	Leaf = 'LEAF',
	Struct = 'STRUCT',
	Array = 'ARRAY',
}

export type DataStructType = EnumValues<typeof SchemaDataStructure>;

export interface IDepotSchema {
	id: DepotSchemaId;
	children: Array<IDepotSchema>;
	originalLabel: string;
	path: string;
	currentLabel: string;
	depth: number;
	isSelected: boolean;
	parent: IDepotSchema | null;
	dataType: ColumnType | null;
	schemaElemType: DataStructType | SchemaDataStructure;
	isSerialized: boolean;
	shouldIgnoreSubtree: boolean;
	position: number;
	convertTo: ColumnType | null;
	format: string | null;
	timezone: string | null;
	language: string | null;
}

type RootDepotSchema = Pick<IDepotSchema, 'children'>;

type PrimitiveDepotSchema = Omit<IDepotSchema, 'schemaElemType'> & {
	structType: SchemaDataStructure.Leaf;
};

type ArrayDepotSchema = {
	id: DepotSchemaId;
	element: IDepotSchema | null;
	path: string;
	originalLabel: string;
	currentLabel: string;
	depth: number;
	isSelected: boolean;
	parent: IDepotSchema | null;
	structType: SchemaDataStructure.Array | 'ARRAY';
	isSerialized: boolean;
	shouldIgnoreSubtree: boolean;
	position: number | undefined;
	convertTo: ColumnType | null;
};

type DepotSchemaValidation = 'duplicate-key';

type DepotSchemaValidationError = {
	type: DepotSchemaValidation;
	schemaHeader: IDepotSchema;
};

type StructDepotSchema = Omit<IDepotSchema, 'schemaElemType'>;

const entityDefaults: IDepotSchema = {
	id: -1,
	children: [],
	path: '',
	originalLabel: '',
	currentLabel: '',
	depth: 0,
	parent: null,
	dataType: ColumnType.Unknown,
	isSelected: false,
	schemaElemType: SchemaDataStructure.Root,
	isSerialized: false,
	shouldIgnoreSubtree: false,
	position: -1,
	convertTo: null,
	format: null,
	timezone: null,
	language: null,
};

export class DepotSchemaEntity implements IDepotSchema {
	private [immerable] = true;
	public id = entityDefaults.id;
	public children = entityDefaults.children;
	public depth = entityDefaults.depth;
	public parent = entityDefaults.parent;
	public path = entityDefaults.path;
	public originalLabel = entityDefaults.originalLabel;
	public currentLabel = entityDefaults.currentLabel;
	public dataType = entityDefaults.dataType;
	public isSelected = entityDefaults.isSelected;
	public schemaElemType = entityDefaults.schemaElemType;
	public isSerialized = entityDefaults.isSerialized;
	public shouldIgnoreSubtree = entityDefaults.shouldIgnoreSubtree;
	public position = entityDefaults.position;
	public convertTo = entityDefaults.convertTo;
	public format = entityDefaults.format;
	public timezone = entityDefaults.timezone;
	public language = entityDefaults.language;

	public static buildRoot(params: RootDepotSchema): DepotSchemaEntity {
		return new DepotSchemaEntity({
			id: '-1',
			children: params.children,
			schemaElemType: SchemaDataStructure.Root,
			parent: null,
			depth: 0,
			dataType: null,
			originalLabel: '',
			path: '',
			isSelected: false,
			isSerialized: false,
		});
	}

	public static buildLeaf(
		params: Partial<PrimitiveDepotSchema>
	): DepotSchemaEntity {
		return new DepotSchemaEntity({
			id: params.id,
			children: [],
			schemaElemType: SchemaDataStructure.Leaf,
			parent: params.parent,
			path: params.path ?? entityDefaults.path,
			depth:
				params.parent === null || params.parent === undefined
					? 0
					: params.parent.depth + 1,
			dataType: params.dataType,
			originalLabel: params.originalLabel,
			currentLabel: params.currentLabel ?? params.originalLabel,
			isSelected: params.isSelected ?? entityDefaults.isSelected,
			isSerialized: false,
			shouldIgnoreSubtree:
				params.shouldIgnoreSubtree ?? entityDefaults.shouldIgnoreSubtree,
			convertTo: params.convertTo ?? entityDefaults.convertTo,
			position: params.position,
			format: params.format ?? entityDefaults.format,
			timezone: params.timezone ?? entityDefaults.timezone,
			language: params.language ?? entityDefaults.language,
		});
	}

	public static buildArray(
		params: Partial<ArrayDepotSchema>
	): DepotSchemaEntity {
		return new DepotSchemaEntity({
			id: params.id,
			dataType: ColumnType.Unknown,
			children: ([params.element] as Array<IDepotSchema>) ?? [],
			schemaElemType: SchemaDataStructure.Array,
			parent: params.parent ?? null,
			path: params.path ?? entityDefaults.path,
			depth:
				params.parent === null || params.parent === undefined
					? 0
					: params.parent.depth + 1,
			originalLabel: params.originalLabel,
			currentLabel: params.currentLabel ?? params.originalLabel,
			isSelected: params.isSelected ?? entityDefaults.isSelected,
			isSerialized: params.isSerialized ?? entityDefaults.isSerialized,
			shouldIgnoreSubtree:
				params.shouldIgnoreSubtree ?? entityDefaults.shouldIgnoreSubtree,
			position: params.position ?? entityDefaults.position,
			convertTo: params.convertTo ?? entityDefaults.convertTo,
		});
	}

	public static buildStruct(
		params: Partial<StructDepotSchema>
	): DepotSchemaEntity {
		return new DepotSchemaEntity({
			id: params.id,
			children: params.children,
			schemaElemType: SchemaDataStructure.Struct,
			parent: params.parent,
			path: params.path ?? entityDefaults.path,
			depth:
				params.parent === null || params.parent === undefined
					? 0
					: params.parent.depth + 1,
			dataType: ColumnType.Struct,
			originalLabel: params.originalLabel,
			currentLabel: params.currentLabel ?? params.originalLabel,
			isSelected: params.isSelected ?? entityDefaults.isSelected,
			isSerialized: params.isSerialized ?? entityDefaults.isSerialized,
			shouldIgnoreSubtree:
				params.shouldIgnoreSubtree ?? entityDefaults.shouldIgnoreSubtree,
			position: params.position ?? entityDefaults.position,
			convertTo: params.convertTo ?? entityDefaults.convertTo,
		});
	}

	public static flatten(
		entity: Array<IDepotSchema>,
		accumulator: Array<DepotSchemaEntity> = []
	): Array<DepotSchemaEntity> {
		return entity.reduce<Array<DepotSchemaEntity>>((acc, curr) => {
			if (curr.schemaElemType === SchemaDataStructure.Root) {
				return DepotSchemaEntity.flatten(curr.children, acc);
			} else if (curr.schemaElemType === SchemaDataStructure.Struct) {
				acc.push(curr as DepotSchemaEntity);
				return DepotSchemaEntity.flatten(curr.children, acc);
			} else if (curr.schemaElemType === SchemaDataStructure.Array) {
				acc.push(curr as DepotSchemaEntity);
				return DepotSchemaEntity.flatten(curr.children, acc);
			} else if (curr.schemaElemType === SchemaDataStructure.Leaf) {
				acc.push(curr as DepotSchemaEntity);
				return acc;
			}
			return acc;
		}, accumulator);
	}

	public static filter(
		entity: IDepotSchema,
		filterValue: string,
		res: Array<IDepotSchema>
	): Array<DepotSchemaEntity> {
		if (entity.schemaElemType === SchemaDataStructure.Root) {
			res.push(
				DepotSchemaEntity.buildRoot({
					children: entity.children.filter((curr) =>
						curr.currentLabel.toLowerCase().includes(filterValue.toLowerCase())
					),
				})
			);
		}
		return res as Array<DepotSchemaEntity>;
	}

	public static analyze(
		struct: Array<IDepotSchema>,
		accumulator = Map<string, Set<string>>()
	): Map<string, Set<string>> {
		return struct.reduce((acc, curr) => {
			const entryLabel = curr.currentLabel
				? curr.currentLabel
				: EMPTY_SCHEMA_HEADER_LABEL;
			const entriesWithSameLabel = acc.get(entryLabel);
			if (entriesWithSameLabel) {
				acc = acc.set(entryLabel, entriesWithSameLabel.add(`${curr.id}`));
			} else {
				acc = curr.currentLabel
					? acc.set(curr.currentLabel, Set.of(`${curr.id}`))
					: acc.set(EMPTY_SCHEMA_HEADER_LABEL, Set.of(`${curr.id}`));
			}
			if (
				curr.schemaElemType === SchemaDataStructure.Root ||
				curr.schemaElemType === SchemaDataStructure.Array ||
				curr.schemaElemType === SchemaDataStructure.Struct
			) {
				acc = DepotSchemaEntity.analyze(curr.children, acc);
			}
			return acc;
		}, accumulator);
	}

	private constructor(params: Partial<IDepotSchema>) {
		this.id = params.id ?? entityDefaults.id;
		this.depth = params.depth ?? entityDefaults.depth;
		this.parent = params.parent ?? entityDefaults.parent;
		this.schemaElemType =
			params.schemaElemType ?? entityDefaults.schemaElemType;
		this.path = params.path ?? entityDefaults.path;
		this.originalLabel = params.originalLabel ?? entityDefaults.originalLabel;
		this.currentLabel = params.currentLabel ?? entityDefaults.currentLabel;
		this.dataType = params.dataType ?? entityDefaults.dataType;
		this.isSelected = params.isSelected ?? entityDefaults.isSelected;
		this.children = params.children ?? entityDefaults.children;
		this.isSerialized = params.isSerialized ?? entityDefaults.isSerialized;
		this.shouldIgnoreSubtree =
			params.shouldIgnoreSubtree ?? entityDefaults.shouldIgnoreSubtree;
		this.position = params.position ?? entityDefaults.position;
		this.convertTo = params.convertTo ?? entityDefaults.convertTo;
		this.format = params.format ?? entityDefaults.format;
		this.timezone = params.timezone ?? entityDefaults.timezone;
		this.language = params.language ?? entityDefaults.language;

		if (params.position) {
		}
	}

	public updateChildren(
		children: DepotSchemaEntity | Array<DepotSchemaEntity>
	): DepotSchemaEntity {
		if (this.schemaElemType === 'LEAF') {
			console.warn('Attempting to update children of a leaf schema header');
			return this;
		}

		let updatedChildren = Array.isArray(children) ? children : [children];
		updatedChildren = updatedChildren.map((childNoParent) =>
			childNoParent.setPath(this.path, childNoParent.originalLabel)
		);
		return produce(this, (draft: DepotSchemaEntity) => {
			draft.children = updatedChildren;
		});
	}

	public setParent(parent: DepotSchemaEntity): DepotSchemaEntity {
		return produce(this, (draft: DepotSchemaEntity) => {
			draft.parent = null;
		});
	}

	public setChildren(
		updatedChildren: Array<DepotSchemaEntity>
	): DepotSchemaEntity {
		return produce(this, (draft: DepotSchemaEntity) => {
			draft.children = updatedChildren;
		});
	}

	public resetLabel(): DepotSchemaEntity {
		return produce(this, (draft: DepotSchemaEntity) => {
			draft.currentLabel = draft.originalLabel;
		});
	}

	public updateDataType(dataType: ColumnType): DepotSchemaEntity {
		return produce(this, (draft: DepotSchemaEntity) => {
			draft.dataType = dataType;
		});
	}

	public updateConvertTo(convertTo: ColumnType | null): DepotSchemaEntity {
		return produce(this, (draft: DepotSchemaEntity) => {
			draft.convertTo = convertTo;
		});
	}

	public updateFormat(format: string | null): DepotSchemaEntity {
		return produce(this, (draft: DepotSchemaEntity) => {
			draft.format = format;
		});
	}

	public updateTimezone(timezone: string | null): DepotSchemaEntity {
		return produce(this, (draft: DepotSchemaEntity) => {
			draft.timezone = timezone;
		});
	}

	public updateLanguage(language: string | null): DepotSchemaEntity {
		return produce(this, (draft: DepotSchemaEntity) => {
			draft.language = language;
		});
	}

	public updatePosition(position: number): DepotSchemaEntity {
		return produce(this, (draft: DepotSchemaEntity) => {
			draft.position = position;
		});
	}

	public updateCurrentLabel(val: string): DepotSchemaEntity {
		return produce(this, (draft: DepotSchemaEntity) => {
			draft.currentLabel = val.trim() ? val : draft.originalLabel;
		});
	}

	public updateSelection(val: boolean): DepotSchemaEntity {
		return produce(this, (draft: DepotSchemaEntity) => {
			// what happens when entry is marked as ignoreSubTree ?
			// no check if serialization should happen for STRUCT and ARRAY type on selection
			// because logic is handled by default in the HTML and parser logic
			draft.isSelected = val;
			if (draft.schemaElemType === 'STRUCT') {
				draft.isSerialized = val;
				draft.convertTo = val ? ColumnType.Str : null;
			}
			if (draft.schemaElemType === 'ARRAY') {
				if (draft.children[0].schemaElemType !== 'LEAF') {
					draft.isSerialized = val;
					draft.convertTo = val ? ColumnType.Str : null;
				} else if (draft.children[0].schemaElemType === 'LEAF') {
					draft.convertTo = val
						? draft.isSerialized
							? ColumnType.Str
							: (draft.children[0].dataType as ColumnType)
						: null;
				}
			}
		});
	}

	public selectIfLeafEntry(val: boolean): DepotSchemaEntity {
		if (
			this.schemaElemType === SchemaDataStructure.Leaf &&
			!this.shouldIgnoreSubtree
		) {
			return produce(this, (draft: DepotSchemaEntity) => {
				draft.isSelected = val;
			});
		}

		return this;
	}

	public updateIsSerialized(val: boolean): DepotSchemaEntity {
		return produce(this, (draft: DepotSchemaEntity) => {
			draft.isSerialized = val;
			draft.dataType = null;
			if (draft.children[0].schemaElemType !== 'LEAF') {
				draft.convertTo = val ? ColumnType.Str : null;
			} else if (draft.children[0].schemaElemType === 'LEAF') {
				draft.convertTo = draft.isSerialized
					? ColumnType.Str
					: (draft.children[0].dataType as ColumnType);
			}
		});
	}

	public replaceLabelWithPath(onSelection = true): DepotSchemaEntity {
		return produce(this, (draft: DepotSchemaEntity) => {
			if (onSelection) {
				draft.currentLabel = draft.isSelected ? draft.path : draft.currentLabel;
				return;
			}
			draft.currentLabel = draft.path;
		});
	}

	public ignoreSubtree(
		val: boolean,
		isStartingEntry = true
	): DepotSchemaEntity {
		return produce(this, (draft: DepotSchemaEntity) => {
			draft.shouldIgnoreSubtree = val;
			if (draft.schemaElemType === SchemaDataStructure.Leaf) {
				draft.isSelected = isStartingEntry;
			} else if (draft.schemaElemType === SchemaDataStructure.Struct) {
				draft.isSelected = isStartingEntry;
				draft.children = draft.children.map<DepotSchemaEntity>((c) =>
					(c as DepotSchemaEntity).ignoreSubtree(val, false)
				);
			} else if (draft.schemaElemType === SchemaDataStructure.Array) {
				draft.isSelected = isStartingEntry;
				draft.children = draft.children.map<DepotSchemaEntity>((c) =>
					(c as DepotSchemaEntity).ignoreSubtree(val, false)
				);
			}
			return draft;
		});
	}

	public setPath(parentLabel: string, elementLabel: string): DepotSchemaEntity {
		return produce(this, (draft: DepotSchemaEntity) => {
			draft.path = this.generatePath(parentLabel, elementLabel);
		});
	}

	public replaceWith(
		searchTerm: string,
		replaceWith: string
	): DepotSchemaEntity {
		return produce(this, (draft: DepotSchemaEntity) => {
			if (!this.shouldIgnoreSubtree) {
				draft.currentLabel = this.currentLabel.replace(
					new RegExp(searchTerm, 'g'),
					replaceWith
				);
			}
			if (draft.schemaElemType !== SchemaDataStructure.Leaf) {
				draft.children = (this.children as Array<DepotSchemaEntity>).map((c) =>
					c.replaceWith(searchTerm, replaceWith)
				);
			}
		});
	}

	private generatePath(parentLabel: string, element: string): string {
		let path;
		if (parentLabel === '' && element !== '') {
			path = element;
		} else if (parentLabel !== '' && element === '') {
			path = `${parentLabel}-${this.depth}`;
		} else if (parentLabel === '' && element === '') {
			path = `-${this.depth}`;
		} else {
			path = `${parentLabel}.${element}`;
		}
		return path;
	}
}
